import { memo, useState } from 'react';
import * as BlackCard from 'root/src/client/v2/common/components/BlackCard/BlackCard';
import { DropUserInfoWidget, FitWidget } from 'root/src/client/v2/_pages/drops/components/DropUserInfoWidget';
import EarnPointsIcon from 'root/src/client/public/icons/IcEarnPoints';
import { useDropEventListContext, } from 'root/src/client/v2/_pages/drops/contexts/dropEventList.context';
import { DropLockStatusWidget, } from 'root/src/client/v2/_pages/drops/components/DropLockStatusWidget';
import defaultPublisherLogo from 'root/src/client/public/default-publisher-logo.png';
import { DarePlatformIcon, DarePlatformIconTooltip, PlatformIconColor, } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { GamePlatformIcon, } from 'root/src/client/v2/common/components/Games/components/GamePlatformIcon';
import { PointsApplicationState } from 'root/src/server/modules/points/types/types';
import { BaseTooltip, BaseTooltipContent, } from 'root/src/client/v2/common/components/BaseTooltip/BaseTooltip';
import { colorsNew } from 'root/src/client/web/commonStyles';
import classnames from 'classnames';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { NewFeatureBadge } from 'root/src/client/v2/common/components/badge/NewFeatureBadge';
import { useIsClipReviewer } from 'root/src/client/v2/auth/hooks/useIsClipReviewer';
import { FaGift } from 'react-icons/fa6';
export const DropAttributes = memo(() => {
    const { isClipReviewer } = useIsClipReviewer();
    const { dropEvent } = useDropEventListContext();
    if (!isClipReviewer || !dropEvent.extraDaresEnabled) {
        return null;
    }
    return (<ul className="flex gap-2">
			<li>
				<BaseTooltip tooltip={(<BaseTooltipContent>
						This Drop awards Clip Reviewers<br /> with extra Dares!
					</BaseTooltipContent>)}>
					<div>
						<NewFeatureBadge featureId="extraDares" x={-0.2}>
							<FaGift />
						</NewFeatureBadge>
					</div>
				</BaseTooltip>
			</li>
		</ul>);
});
export const DropsListItem = memo(() => {
    const [isBodyHovered, setBodyHover] = useState(false);
    return (<BlackCard.Root onMouseLeave={() => setBodyHover(false)} className="drop-card cursor-pointer animate-[slide-up_.3s] !h-[460px]">
			<BlackCard.Header className={classnames('h-[30%] duration-150', isBodyHovered && 'translate-y-[-140px]')}>
				<DropImage />
			</BlackCard.Header>
			<BlackCard.Separator className={classnames('flex flex-initial h-8 justify-around px-2 py-0.5 gap-2 items-center duration-150', isBodyHovered && 'translate-y-[-140px]')} onMouseEnter={() => setBodyHover(false)} onMouseLeave={() => setBodyHover(false)}>
				<div className="flex justify-between items-center">
					<BrandDetails />
					<DropAttributes />
				</div>
			</BlackCard.Separator>
			<BlackCard.Body onMouseEnter={() => setBodyHover(true)} className={classnames('px-4 pt-6 gap-2 duration-150', isBodyHovered && '!overflow-visible translate-y-[-140px]')}>
				<div className={classnames('flex flex-initial flex-col gap-2')}>
					<DropPreview />
				</div>
				<div className={classnames('hidden flex-initial flex-col gap-2 duration-150', isBodyHovered && '!flex')}>
					<DropDetails />
				</div>
				<div className={classnames('hidden duration-150', isBodyHovered && '!flex')}>
					<DropUserInfoWidget />
				</div>
			</BlackCard.Body>
			<BlackCard.Footer onMouseEnter={() => setBodyHover(true)} className={classnames(isBodyHovered && 'translate-y-[46px]', '!h-[10%] duration-150')}>
				<div className={classnames('flex justify-center flex-col items-center')}>
					<KeyboardDoubleArrowUpIcon />
				</div>
			</BlackCard.Footer>
		</BlackCard.Root>);
});
const DropPointsIcon = memo(() => {
    const { fill, message } = useDropPointsIcon();
    return (<BaseTooltip delay={0} tooltip={message && (<BaseTooltipContent className="flex flex-col justify-center items-center">
					{message}
				</BaseTooltipContent>)}><EarnPointsIcon fill={fill}/>
		</BaseTooltip>);
});
const useDropPointsIcon = () => {
    const { dropEvent } = useDropEventListContext();
    const { pointsApplicationState, isPointsReservationComplete } = dropEvent.dropUserInfo || {};
    if (isPointsReservationComplete) {
        return {
            fill: colorsNew.primary,
            message: 'Your points reservation was completed!',
        };
    }
    if (pointsApplicationState === PointsApplicationState.APPROVED) {
        return {
            fill: colorsNew.green,
            message: 'Your points application was approved',
        };
    }
    if (pointsApplicationState === PointsApplicationState.REJECTED) {
        return {
            fill: colorsNew.red,
            message: 'Your points application was rejected',
        };
    }
    if (pointsApplicationState === PointsApplicationState.PENDING) {
        return {
            fill: colorsNew.yellow,
            message: 'Your points application is in review',
        };
    }
    return {
        fill: colorsNew.teal,
        message: 'Drop awards points',
    };
};
const PrivateDropWidget = memo(() => {
    return (<div className="font-mono group-hover:hidden absolute self-center items-center uppercase font-bold rounded bg-primary px-2 py-1 shadow drop-shadow">
			Private
		</div>);
});
const DropImage = memo(() => {
    var _a;
    const { dropEvent } = useDropEventListContext();
    return (<div className="flex flex-col justify-center items-center flex-initial w-full h-full">
			<img 
    /* eslint-disable-next-line react/no-unknown-property */
    fetchPriority="high" loading="lazy" width={120} height={120} className="overflow-hidden flex flex-initial object-cover object-top self-start group-hover:scale-110 group-hover:-rotate-2 motion-safe:duration-150 brightness-50 w-full h-full" src={dropEvent.image} alt={dropEvent.mnemonicId}/>
			{dropEvent.awardPoints
            && (((_a = dropEvent.dropUserInfo) === null || _a === void 0 ? void 0 : _a.pointsApplicationState) !== PointsApplicationState.REJECTED || dropEvent.isCreatorPointsAllowlisted) && (<div className="absolute top-1 left-2">
					<DropPointsIcon />
				</div>)}
			{dropEvent.lockStatus && (<div className="absolute top-1 right-2">
					<DropLockStatusWidget isLocked={dropEvent.lockStatus.isLocked} description={dropEvent.lockStatus.description}/>
				</div>)}
			{dropEvent.isPrivate && (<PrivateDropWidget />)}
			<FitWidget />
		</div>);
});
const BrandDetails = memo(() => {
    var _a;
    const { dropEvent } = useDropEventListContext();
    return (<>
			<div className="flex relative items-center">
				<img width={70} height={70} className="flex flex-initial self-left justify-left max-w-12 max-h-12 rounded-full shadow-[0_0_10px_2px_#00FFED] drop-shadow-[0_0_8px_#00FFED] border-2 border-white" src={(_a = dropEvent.brand.img) !== null && _a !== void 0 ? _a : defaultPublisherLogo.src} alt={dropEvent.brand.name}/>
			</div>
			<span className="flex flex-initial w-full text-xs p-1">{dropEvent.brand.name}</span>
		</>);
});
const DropPreview = memo(() => {
    const { dropEvent } = useDropEventListContext();
    return (<>
			<p className="font-bold font-mono text-teal line-clamp-3">{dropEvent.label}</p>
			<p className="text-xs leading-5 overflow-x-hidden text-ellipsis line-clamp-4">{dropEvent.description}</p>
			<ul role="list" className="flex flex-initial gap-2">
				{dropEvent.platforms.map(platform => (<li key={platform}>
						<DarePlatformIconTooltip variant="light" platform={platform}>
							<div className="flex bg-white p-1 rounded-full w-6 h-6 flex justify-center items-center flex-initial">
								<DarePlatformIcon size="16" platform={platform} color={PlatformIconColor.Dark}/>
							</div>
						</DarePlatformIconTooltip>
					</li>))}
			</ul>
		</>);
});
const DropDetails = memo(() => {
    return (<div className="flex flex-col gap-1">
			<GameGenres />
			<GamePlatforms />
		</div>);
});
const GamePlatforms = memo(() => {
    const { dropEvent } = useDropEventListContext();
    const { gamePlatforms } = dropEvent;
    if (!(gamePlatforms === null || gamePlatforms === void 0 ? void 0 : gamePlatforms.length)) {
        return null;
    }
    return (<BlackCard.HighlightedSection title="Game platforms" text={(<ul className="flex gap-2">
					{gamePlatforms.map((platform) => {
                return (<GamePlatformIcon size="2x" key={platform} platform={platform}/>);
            })}
				</ul>)}/>);
});
const GameGenres = memo(() => {
    const { dropEvent } = useDropEventListContext();
    const { genres } = dropEvent;
    if (!genres.length) {
        return null;
    }
    return (<BlackCard.HighlightedSection title="Genres" text={<p className="text-xs overflow-x-hidden text-ellipsis line-clamp-2">{genres.join(', ')}</p>}/>);
});
